import { ChangeDetectionStrategy, Component, ElementRef, ViewChild, computed, effect, inject, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import {
  ColimindSettingsService,
  MAX_ADDRESSING_LEN,
  MAX_SUGGESTION_INSTRUCTION_LEN,
  MAX_SUGGESTION_TITLE_LEN,
  MAX_SYSTEM_PROMPT_LEN,
} from '../service/colimind-settings.service';
import { DEFAULT_SUGGESTIONS } from '../default-suggestions';
import { ColimindDebugService } from '../../../services/colimind/colimind-debug.service';

@Component({
  selector: 'colimo-settings-panel',
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './colimind-settings-panel.component.html',
  styleUrls: ['./colimind-settings-panel.component.scss'],
})
export class ColimindSettingsPanelComponent {
  readonly settings = inject(ColimindSettingsService);
  readonly debug = inject(ColimindDebugService);
  readonly maxSystemPromptLength = MAX_SYSTEM_PROMPT_LEN;
  readonly maxAddressingLength = MAX_ADDRESSING_LEN;
  readonly maxTitleLength = MAX_SUGGESTION_TITLE_LEN;
  readonly maxInstructionLength = MAX_SUGGESTION_INSTRUCTION_LEN;
  readonly defaultSuggestions = DEFAULT_SUGGESTIONS;

  // The backend does not pass the instructions to the agent yet, so they only show in debug mode
  readonly instructionsAvailable = false;

  /**
   * The profile fields are edited as a draft and only written on Save, as the design shows.
   * Everything else in the panel (suggestions, active toggles) still takes effect immediately,
   * because those are single deliberate clicks rather than free text being typed.
   */
  readonly draftInstructions = signal('');
  readonly draftAddressing = signal('');

  /** Suggestion form is hidden until the user asks for it via "Add suggestion". */
  readonly addingSuggestion = signal(false);

  /**
   * The draft card is appended to the grid, below the profile section, so opening it from the
   * chat bookmark would leave it off screen. Scrolling it into view and focusing the title
   * makes the form findable without hunting for it.
   */
  @ViewChild('draftTitleInput') set draftTitleInput(input: ElementRef<HTMLInputElement> | undefined) {
    if (!input) return;
    input.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    input.nativeElement.focus({ preventScroll: true });
  }

  readonly instructionsLength = computed(() => this.draftInstructions().trim().length);
  readonly instructionsTooLong = computed(() => this.instructionsLength() > MAX_SYSTEM_PROMPT_LEN);

  readonly dirty = computed(() =>
    this.draftInstructions() !== this.settings.settings().systemPrompt ||
    this.draftAddressing() !== this.settings.settings().addressing);

  readonly canSave = computed(() => this.dirty() && !this.instructionsTooLong());

  /** Stored values as last adopted, to tell an external change from the user typing. */
  private syncedInstructions = '';
  private syncedAddressing = '';

  constructor() {
    // Adopts stored values when they actually change, e.g. once the profile has finished
    // loading. Comparing against the last adopted value rather than against the draft is what
    // keeps an edit in progress alive: typing never changes the stored value.
    effect(() => {
      const stored = this.settings.settings();
      if (stored.systemPrompt !== this.syncedInstructions) {
        this.syncedInstructions = stored.systemPrompt;
        this.draftInstructions.set(stored.systemPrompt);
      }
      if (stored.addressing !== this.syncedAddressing) {
        this.syncedAddressing = stored.addressing;
        this.draftAddressing.set(stored.addressing);
      }
    }, { allowSignalWrites: true });

    // The bookmark on a chat message fills the suggestion draft and switches to this panel.
    // Without opening the form the user would land on a settings page that looks unchanged,
    // with their prompt stored invisibly.
    effect(() => {
      const hasDraft = Boolean(this.settings.draftTitle().trim() || this.settings.draftInstruction().trim());
      if (hasDraft) this.addingSuggestion.set(true);
    }, { allowSignalWrites: true });
  }

  defaultActive(id: string): boolean {
    return !this.settings.hiddenDefaults().includes(id);
  }

  save(): void {
    if (!this.canSave()) return;
    this.settings.update({
      systemPrompt: this.draftInstructions(),
      addressing: this.draftAddressing(),
    });
  }

  cancel(): void {
    const stored = this.settings.settings();
    this.draftInstructions.set(stored.systemPrompt);
    this.draftAddressing.set(stored.addressing);
  }

  startAddingSuggestion(): void {
    this.settings.clearDraft();
    this.addingSuggestion.set(true);
  }

  cancelAddingSuggestion(): void {
    this.settings.clearDraft();
    this.addingSuggestion.set(false);
  }

  addSuggestion(): void {
    if (this.settings.addDraftAsSuggestion()) {
      this.addingSuggestion.set(false);
    }
  }
}
